import { useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { PageHeader } from '../portfolio/PortfolioCommon'
import { Breadcrumbs } from '../components/ui/Breadcrumbs'
import { stateLabel } from '../lib/display-labels'
import { useTenant } from '../tenancy/TenantProvider'
import { answerAssessmentInformationRequest, getInformationRequestTask } from './assessment-api'

function text(value: unknown): string { return typeof value === 'string' ? value : '' }

const cancellationLabels: Record<string, string> = {
  information_no_longer_required: 'Information no longer required',
  wrong_person: 'Asked the wrong person',
  entered_in_error: 'Request entered in error',
  assessment_changed: 'Assessment changed',
  other: 'Other',
}

export function InformationRequestPage() {
  const { requestId = '' } = useParams()
  const { current } = useTenant()
  const queryClient = useQueryClient()
  const [response, setResponse] = useState('')
  const [evidenceReference, setEvidenceReference] = useState('')
  const taskQuery = useQuery({
    queryKey: ['r1-information-request-task', requestId],
    queryFn: () => getInformationRequestTask(requestId),
    enabled: Boolean(requestId),
  })
  const answerMutation = useMutation({
    mutationFn: () => answerAssessmentInformationRequest(requestId, response, evidenceReference),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['r1-information-request-task', requestId] }),
        queryClient.invalidateQueries({ queryKey: ['r1-my-actions'] }),
        queryClient.invalidateQueries({ queryKey: ['r1-confirmation-task'] }),
      ])
    },
  })
  const task = taskQuery.data
  const error = taskQuery.error ?? answerMutation.error

  useEffect(() => {
    if (error) console.error('Information Request operation failed', error)
  }, [error])

  if (taskQuery.isLoading) return <div className="page-stack"><section className="panel"><p>Loading Information Request…</p></section></div>
  if (!task) return <div className="page-stack"><section className="panel assessment-safe-error" role="alert"><h1>Information Request not available</h1><p>This request may be closed, reassigned or unavailable to your account.</p></section></div>

  const request = task.request
  const aiUse = task.aiUseCase
  const isAnswered = text(request.status) === 'answered'
  const isCancelled = text(request.status) === 'cancelled'
  const cancellationLabel = cancellationLabels[text(request.cancellationReason) || text(request.cancellation_reason)]
  const cancellationExplanation = text(request.cancellationExplanation) || text(request.cancellation_explanation)
  const recipientName = text(request.recipientName) || 'the recipient'
  const isRequester = current?.membershipId === text(request.requestedByMembershipId)
  const relationshipDescription = isRequester
    ? `You asked ${recipientName} for information.${text(request.status) === 'open' ? ` Waiting for ${recipientName} to respond.` : ''}`
    : `${task.requester || 'A specialist'} asked you for information.`
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (response.trim()) answerMutation.mutate()
  }

  return <div className="page-stack information-request-page">
    <Breadcrumbs items={[
      { label: 'AI Uses', to: '/ai-register' },
      { label: text(aiUse.title), to: `/ai-register/${text(aiUse.id)}` },
      ...(task.confirmationDeepLinkPath ? [{ label: 'Specialist confirmation', to: task.confirmationDeepLinkPath }] : []),
      { label: 'Information requested' },
    ]} />
    <PageHeader
      eyebrow="My actions"
      title={`Information requested — ${text(aiUse.title)}`}
      description={relationshipDescription}
    />
    {error ? <section className="inline-alert danger" role="alert"><strong>We couldn't send your response.</strong><p>Your text has been kept on this page. Please try again.</p></section> : null}

    <section className="panel information-request-question" aria-labelledby="information-request-question-heading">
      <p className="eyebrow">Question</p>
      <h2 id="information-request-question-heading">{text(request.question)}</h2>
    </section>

    <section className="panel information-request-context" aria-labelledby="information-request-context-heading">
      <h2 id="information-request-context-heading">About this AI use</h2>
      <p>{text(aiUse.businessPurpose) || 'No business-purpose summary is recorded.'}</p>
      <dl className="decision-brief-facts">
        <div><dt>AI use</dt><dd>{text(aiUse.title)}</dd></div>
        <div><dt>Operational status</dt><dd>{stateLabel(text(aiUse.operationalState))}</dd></div>
      </dl>
    </section>

    {task.canRespond ? <section className="panel" aria-labelledby="information-response-heading">
      <h2 id="information-response-heading">Your response</h2>
      <form className="form-stack" onSubmit={submit}>
        <label>Your response
          <textarea rows={7} required value={response} onChange={(event) => setResponse(event.target.value)} />
        </label>
        <label>Evidence or document reference <span className="muted">(optional)</span>
          <input value={evidenceReference} onChange={(event) => setEvidenceReference(event.target.value)} placeholder="Document, ticket or link reference" />
        </label>
        <button className="button primary" type="submit" disabled={!response.trim() || answerMutation.isPending}>
          {answerMutation.isPending ? 'Sending…' : 'Send response'}
        </button>
      </form>
    </section> : <section className={isAnswered || isCancelled
      ? `panel information-request-closed${isAnswered ? ' answered' : ' cancelled'}`
      : 'panel information-request-read-only'} role="status">
      <h2>{isAnswered ? 'Response received' : isCancelled ? 'Request cancelled' : 'Response required'}</h2>
      <p>{isAnswered
        ? 'This response is part of the governed Assessment record and cannot be submitted again.'
        : isCancelled
          ? 'No response is required. This request remains in the governed Assessment history.'
          : 'This request is still open. Only the current recipient can send the response.'}</p>
      {isCancelled && cancellationLabel ? <p><strong>Reason:</strong> {cancellationLabel}</p> : null}
      {isCancelled && cancellationExplanation ? <p>{cancellationExplanation}</p> : null}
      {text(request.responseText) ? <><h3>Your recorded response</h3><p>{text(request.responseText)}</p></> : null}
      {text(request.evidenceReference) ? <p><strong>Evidence or document reference:</strong> {text(request.evidenceReference)}</p> : null}
    </section>}
  </div>
}
